import { useState, useEffect, useMemo, useRef } from "react";
import ShadowDOM from "react-shadow";

import styles from "./index.css?inline";
import { setValue, getValue } from "@/utils/storage";
import { getServiceStatus } from "@/services/api";
import LiveKitProvider from "@/providers/LiveKitProvider";
import { useGlobalState } from "@/hooks";
import { ShadowRootContext } from "./hooks/use-shadow-root";
import Step1 from "./step1";
import Step2 from "./step2";
import StepAgentClosed from "./stepAgentClosed";
import { AgentTimeoutProvider } from "./providers/AgentTimeoutProvider";
import { Config } from "./types/config";
import { WidgetWrapper } from "./components/WidgetWrapper";

const AGENT_OPEN_TIMEOUT = 30 * 60 * 1000;
// Rotate clients away from rooms created before the connection-lifecycle fix.
// Bump this whenever the deployed voice worker requires a fresh LiveKit room.
// Existing visitors otherwise keep their cached room and can remain attached
// to the previous worker after a backend rollout.
const TEXT_TRANSPORT_VERSION = "livekit-rpc-v12";

type AppProps = Config;

const App = ({ shop, customer, widget }: AppProps) => {
  const [currentStep, setCurrentStep] = useState(getDefaultStep());
  const [isServiceActive, setServiceStatus] = useState(false);
  const { connect, setShowReconnectPrompt, setConfig } = useGlobalState();
  const customerId = useMemo(() => {
    const existingId = customer.id || getValue("customer_id");
    if (!existingId || existingId === "null") {
      const randomId = Math.floor(Math.random() * 1000000).toString();
      setValue("customer_id", randomId);
      return randomId;
    }

    return existingId;
  }, [customer.id]);
  const shadowRef = useRef<HTMLDivElement>(null);
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);

  useEffect(() => {
    if (shadowRef?.current && !shadowRoot) {
      setShadowRoot(shadowRef.current.shadowRoot);
    }
  }, [shadowRef?.current]);

  useEffect(() => {
    setConfig({
      shop,
      customer: {
        id: customerId,
      },
      widget,
    });
  }, [shop, customerId]);

  useEffect(() => {
    const check = async () => {
      const status = await getServiceStatus(shop.domain);

      setServiceStatus(status);
    };

    check();
  }, []);

  useEffect(() => {
    if (currentStep === 2) {
      const activeTransportVersion = getValue("text_transport_version");
      const requiresFreshAgent =
        activeTransportVersion !== TEXT_TRANSPORT_VERSION;

      setValue("text_transport_version", TEXT_TRANSPORT_VERSION);
      connect(requiresFreshAgent ? { newRoom: true } : undefined);
    }
  }, [currentStep]);

  if (!isServiceActive) {
    return null;
  }

  return (
    <ShadowDOM.div id="nut-widget-shadow-root" ref={shadowRef}>
      <style type="text/css">{styles}</style>
      <ShadowRootContext.Provider value={shadowRoot}>
        {currentStep === 0 ? (
          <WidgetWrapper margins={widget?.margins}>
            <StepAgentClosed setStep={setCurrentStep} />
          </WidgetWrapper>
        ) : (
          <LiveKitProvider>
            <AgentTimeoutProvider
              onTimeout={() => setShowReconnectPrompt(true)}
            >
              <WidgetWrapper margins={widget?.margins}>
                {currentStep === 1 ? (
                  <Step1 setStep={setCurrentStep} />
                ) : currentStep === 2 ? (
                  <Step2 setStep={setCurrentStep} />
                ) : null}
              </WidgetWrapper>
            </AgentTimeoutProvider>
          </LiveKitProvider>
        )}
      </ShadowRootContext.Provider>
    </ShadowDOM.div>
  );
};

export default App;

const getDefaultStep = () => {
  const lastInteraction = Number(getValue("last_interaction"));
  const lastVisit = Number(getValue("last_visit"));

  const mostRecentActivity = Math.max(lastInteraction, lastVisit);
  setValue("last_visit", Date.now().toString());

  // Default agent to open if last interaction was more than timeout
  if (
    !mostRecentActivity ||
    Date.now() - mostRecentActivity > AGENT_OPEN_TIMEOUT
  ) {
    return 1;
  }

  return 0;
};
